import { User, onAuthStateChanged } from "firebase/auth";
import { ReactNode, createContext, useContext, useEffect, useState } from "react";
import { auth } from "firebase-utils/firebase";
import { logger } from 'utils/logger';

interface AuthContextProps {
  currentUser: User | null;
  userLoggedIn: boolean;
  loading: boolean;
  roles: Array<string>;
  orgs: Array<string>;
}

const DEFAULT_CONTEXT: AuthContextProps = {
  currentUser: null,
  userLoggedIn: false,
  loading: true,
  roles: [],
  orgs: []
};

const AuthContext = createContext<AuthContextProps>(DEFAULT_CONTEXT);

export function useAuth() {
  return useContext(AuthContext);
}

function toStringArray(value: unknown): Array<string> {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userLoggedIn, setUserLoggedIn] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [roles, setRoles] = useState<Array<string>>([]);
  const [orgs, setOrgs] = useState<Array<string>>([]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const token = await user.getIdTokenResult();
          setCurrentUser({...user});
          setUserLoggedIn(true);
          setRoles(toStringArray(token.claims['roles']));
          setOrgs(toStringArray(token.claims['orgs']));
        } catch (error) {
          logger.error('Failed to retrieve auth token', { context: 'AuthContext', error });
          setCurrentUser(null);
          setUserLoggedIn(false);
          setRoles([]);
          setOrgs([]);
        }
      } else {
        setCurrentUser(null);
        setUserLoggedIn(false);
        setRoles([]);
        setOrgs([]);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, [])

  return (
    <AuthContext.Provider value={{ currentUser, userLoggedIn, loading, roles, orgs }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
